/**
 * Session time helpers — treat backend `scheduled_date` + `scheduled_time` as IST
 * and convert to local time for display.
 *
 * Backend stores: `scheduled_date: "2026-04-22"` + `scheduled_time: "18:55"` (IST)
 * Browser: auto-renders in the user's local timezone via Date().toLocaleString()
 */

/** Parse backend session timestamp as an IST Date object. */
export function sessionStart(session) {
  if (!session?.scheduled_date || !session?.scheduled_time) return null;
  return new Date(`${session.scheduled_date}T${session.scheduled_time}:00+05:30`);
}

/** End-of-session datetime. */
export function sessionEnd(session) {
  const start = sessionStart(session);
  if (!start) return null;
  const duration = session.duration_minutes || 60;
  return new Date(start.getTime() + duration * 60 * 1000);
}

/** Minutes until session starts (negative = already started). */
export function minutesUntilStart(session) {
  const start = sessionStart(session);
  if (!start) return null;
  return (start.getTime() - Date.now()) / 60000;
}

/** Minutes past session end (positive = ended). */
export function minutesPastEnd(session) {
  const end = sessionEnd(session);
  if (!end) return null;
  return (Date.now() - end.getTime()) / 60000;
}

/** Can instructor start? Within `bufferMins` (default 10) of scheduled start. */
export function canInstructorStart(session, bufferMins = 10) {
  const m = minutesUntilStart(session);
  if (m === null) return false;
  // Allow start from `bufferMins` before until end of session
  const endMinsPast = minutesPastEnd(session) ?? 0;
  return m <= bufferMins && endMinsPast < 0; // before end
}

/** Can learner join? Wider buffer (default 15 mins before; until end of session). */
export function canLearnerJoin(session, bufferMins = 15) {
  const m = minutesUntilStart(session);
  if (m === null) return false;
  const endMinsPast = minutesPastEnd(session) ?? 0;
  return m <= bufferMins && endMinsPast < 0;
}

/** Display session time in user's local timezone (e.g. "Mon, 22 Apr · 18:55 IST (6:55 PM)"). */
export function formatSessionTime(session, opts = {}) {
  const start = sessionStart(session);
  if (!start) return '';
  const { showIST = true } = opts;
  const local = start.toLocaleString('en-IN', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  if (!showIST) return local;
  const ist = start.toLocaleString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Kolkata',
  });
  // If user IS in IST, skip the "(IST)" parenthetical
  const userIsIST = new Date().getTimezoneOffset() === -330;
  return userIsIST ? local : `${local} (${ist} IST)`;
}

/** Format just the date part (weekday + day + month). */
export function formatSessionDate(session) {
  const start = sessionStart(session);
  if (!start) return '';
  return start.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' });
}

/** Format just the time in user's local timezone. */
export function formatLocalTime(session) {
  const start = sessionStart(session);
  if (!start) return '';
  return start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true });
}
